import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { changeCategoryStatusAPI, createCategoryAPI, fetchCategoryByIdAPI, fetchCategoryListAPI, getAllCategoryListAPI, updateCategoryAPI } from '../apis/category.api.js';
import { successToastify } from '../utils/toastify.util.js';

const baseFields = {
    id: "",
    name: "",
    description: "",
    logo: "",
    banner: "",
    coverimage: ""
}

const initialState = {
    isLoad: false,
    isBtnLoad: false,
    isRedirect: false,
    isUpdate: false,
    listData: [],
    totalCount: 0,
    status: 'idle',
    error: null,
    fields: baseFields,
    detailsData: null,
    allCategory: []
};

// Async thunk for fetching categories
export const fetchCategories = createAsyncThunk(
    'category/fetchCategories',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchCategoryListAPI(payload);
            return response?.result || { list: [], totalCount: 0 };
        } catch (err) {
            return rejectWithValue(err?.response?.data || { message: "Unable to load categories." });
        }
    }
);

export const createCategory = createAsyncThunk(
    "category/createCategory",
    async (payload, thunkAPI) => {
        try {
            const response = await createCategoryAPI(payload);
            if (response.success) {
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchCategories(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const fetchCategoryDataById = createAsyncThunk(
    "category/fetchCategoryDataById",
    async (payload) => {
        try {
            const response = await fetchCategoryByIdAPI(payload);
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const updateCategory = createAsyncThunk(
    "category/updateCategory",
    async (payload, thunkAPI) => {
        try {
            const response = await updateCategoryAPI(payload);
            if (response.success) {
                successToastify("Category updated successfully");
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchCategories(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const updateCategoryStatus = createAsyncThunk(
    "category/updateCategoryStatus",
    async (payload, thunkAPI) => {
        try {
            const response = await changeCategoryStatusAPI(payload);
            if (response.success) {
                if (payload.status === 1) {
                    successToastify("Category activated successfully");
                } else if (payload.status === 0) {
                    successToastify("Category deactivated successfully");
                } else {
                    successToastify("Category deleted successfully");
                }
                let req = {
                    limit: 10,
                    offset: 0,
                    searchText: ""
                }
                thunkAPI.dispatch(fetchCategories(req));
            }
            return response;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
)

export const getAllCategories = createAsyncThunk(
    'category/getAllCategories',
    async () => {
        try {
            const response = await getAllCategoryListAPI();
            return response.result;
        } catch (err) {
            const { data } = err.response;
            return data;
        }
    }
);

const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {
        setRedirect: (state, action) => {
            state.isRedirect = action.payload;
        },
        setUpdate: (state, action) => {
            state.isUpdate = action.payload;
        },
        setPlanDetails: (state, action) => {
            state.detailsData = action.payload;
        },
        setFields: (state, action) => {
            state.fields = action.payload;
        },
        setBaseFields : (state, action) => {
            state.fields = baseFields;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.status = 'loading';
                state.isLoad = true;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoad = false;
                state.isRedirect = false;
                const body = action.payload && typeof action.payload === "object" ? action.payload : {};
                state.listData = Array.isArray(body.list) ? body.list : [];
                state.totalCount = Number(body.totalCount) || 0;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoad = false;
                state.error = action.payload?.message || action.error.message;
            });


        builder.addCase(createCategory.pending, (state) => {
            state.isBtnLoad = true;
        });
        builder.addCase(createCategory.fulfilled, (state) => {
            state.isBtnLoad = false;
            state.isRedirect = true;
        });

        builder.addCase(fetchCategoryDataById.pending, (state) => {
            state.isLoad = true;
        });
        builder.addCase(fetchCategoryDataById.fulfilled, (state, action) => {
            state.isLoad = false;
            state.fields = action.payload.result;
        });

        builder.addCase(updateCategory.pending, (state) => {
            state.isBtnLoad = true;
        });
        builder.addCase(updateCategory.fulfilled, (state) => {
            state.isBtnLoad = false;
            state.isRedirect = true;
            state.fields = baseFields;
        });

        builder.addCase(getAllCategories.pending, (state) => {
            state.isLoad = true;
        });
        builder.addCase(getAllCategories.fulfilled, (state, action) => {
            state.isLoad = false;
            const payload = action.payload;
            state.allCategory = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.list)
                  ? payload.list
                  : [];
        });

    },
});

export const { setRedirect, setUpdate, setPlanDetails, setFields, setBaseFields } =
    categorySlice.actions;

export default categorySlice.reducer;
